const FILES = 'abcdefgh';

function isUppercasePiece(piece) {
  return piece === piece.toUpperCase();
}

function isWhitePiece(piece) {
  return Boolean(piece) && isUppercasePiece(piece);
}

function isBlackPiece(piece) {
  return Boolean(piece) && !isUppercasePiece(piece);
}

function sameColor(a, b) {
  if (!a || !b) return false;
  return (isWhitePiece(a) && isWhitePiece(b)) || (isBlackPiece(a) && isBlackPiece(b));
}

function inBounds(x, y) {
  return x >= 0 && x < 8 && y >= 0 && y < 8;
}

export function squareToCoords(square) {
  const s = String(square || '').trim().toLowerCase();
  if (s.length !== 2) return null;
  const x = FILES.indexOf(s[0]);
  const rank = Number(s[1]);
  if (x < 0 || rank < 1 || rank > 8) return null;
  return { x, y: 8 - rank };
}

export function coordsToSquare(x, y) {
  if (!inBounds(x, y)) return null;
  return `${FILES[x]}${8 - y}`;
}

function parsePlacementToBoard(placement) {
  const rows = String(placement || '').trim().split('/');
  if (rows.length !== 8) return null;

  const board = rows.map((row) => {
    const out = [];
    for (const ch of row) {
      const n = Number(ch);
      if (Number.isFinite(n) && n > 0) {
        for (let i = 0; i < n; i += 1) out.push(null);
      } else {
        out.push(ch);
      }
    }
    return out.length === 8 ? out : null;
  });

  return board.some((r) => !r) ? null : board;
}

export function parseFen(fen) {
  const raw = String(fen || '').trim();
  if (!raw) return null;
  const parts = raw.split(/\s+/);
  const placement = parts[0];
  const turn = parts[1] || 'w';
  const castling = parts[2] || '-';
  const enPassant = parts[3] || '-';
  const board = parsePlacementToBoard(placement);
  if (!board) return null;
  return { placement, turn, castling, enPassant, board };
}

export function getPieceAt(fen, square) {
  const parsed = parseFen(fen);
  if (!parsed) return null;
  const c = squareToCoords(square);
  if (!c) return null;
  return parsed.board[c.y][c.x] || null;
}

function pushIfValidTarget(board, piece, x, y, out) {
  if (!inBounds(x, y)) return;
  const target = board[y][x];
  if (!target || !sameColor(piece, target)) {
    const sq = coordsToSquare(x, y);
    if (sq) out.push(sq);
  }
}

function collectSliderMoves(board, piece, x, y, dirs) {
  const out = [];
  for (let i = 0; i < dirs.length; i += 1) {
    const [dx, dy] = dirs[i];
    let cx = x + dx;
    let cy = y + dy;
    while (inBounds(cx, cy)) {
      const target = board[cy][cx];
      if (!target) {
        out.push(coordsToSquare(cx, cy));
      } else {
        if (!sameColor(piece, target)) out.push(coordsToSquare(cx, cy));
        break;
      }
      cx += dx;
      cy += dy;
    }
  }
  return out.filter(Boolean);
}

function collectPawnMoves(board, piece, x, y, enPassantSquare) {
  const out = [];
  const white = isWhitePiece(piece);
  const dir = white ? -1 : 1;
  const startRank = white ? 6 : 1;

  const oneY = y + dir;
  if (inBounds(x, oneY) && !board[oneY][x]) {
    out.push(coordsToSquare(x, oneY));
    const twoY = y + dir * 2;
    if (y === startRank && inBounds(x, twoY) && !board[twoY][x]) {
      out.push(coordsToSquare(x, twoY));
    }
  }

  const captureX = [x - 1, x + 1];
  for (let i = 0; i < captureX.length; i += 1) {
    const cx = captureX[i];
    const cy = y + dir;
    if (!inBounds(cx, cy)) continue;
    const target = board[cy][cx];
    if (target && !sameColor(piece, target)) {
      out.push(coordsToSquare(cx, cy));
      continue;
    }
    const sq = coordsToSquare(cx, cy);
    if (sq && enPassantSquare && sq === enPassantSquare) {
      out.push(sq);
    }
  }

  return out.filter(Boolean);
}

function collectKingMoves(board, piece, x, y, castling, turn) {
  const out = [];
  const deltas = [
    [-1, -1], [0, -1], [1, -1],
    [-1, 0],           [1, 0],
    [-1, 1],  [0, 1],  [1, 1]
  ];
  for (let i = 0; i < deltas.length; i += 1) {
    const [dx, dy] = deltas[i];
    pushIfValidTarget(board, piece, x + dx, y + dy, out);
  }

  // Pseudo-legal castling (no "through check" validation).
  if (String(piece).toLowerCase() !== 'k') return out;
  if (turn === 'w' && piece === 'K' && x === 4 && y === 7) {
    if (castling.includes('K') && !board[7][5] && !board[7][6]) out.push('g1');
    if (castling.includes('Q') && !board[7][1] && !board[7][2] && !board[7][3]) out.push('c1');
  }
  if (turn === 'b' && piece === 'k' && x === 4 && y === 0) {
    if (castling.includes('k') && !board[0][5] && !board[0][6]) out.push('g8');
    if (castling.includes('q') && !board[0][1] && !board[0][2] && !board[0][3]) out.push('c8');
  }
  return out;
}

export function getPseudoLegalMovesForSquare(fen, square, options = {}) {
  const parsed = parseFen(fen);
  if (!parsed) return [];
  const c = squareToCoords(square);
  if (!c) return [];
  const piece = parsed.board[c.y][c.x];
  if (!piece) return [];

  const respectTurn = options.respectTurn !== false;
  if (respectTurn) {
    if (parsed.turn === 'w' && !isWhitePiece(piece)) return [];
    if (parsed.turn === 'b' && !isBlackPiece(piece)) return [];
  }

  const kind = piece.toLowerCase();
  if (kind === 'p') return collectPawnMoves(parsed.board, piece, c.x, c.y, parsed.enPassant);
  if (kind === 'n') {
    const out = [];
    const jumps = [
      [-2, -1], [-1, -2], [1, -2], [2, -1],
      [-2, 1],  [-1, 2],  [1, 2],  [2, 1]
    ];
    for (let i = 0; i < jumps.length; i += 1) {
      const [dx, dy] = jumps[i];
      pushIfValidTarget(parsed.board, piece, c.x + dx, c.y + dy, out);
    }
    return out;
  }
  if (kind === 'b') {
    return collectSliderMoves(parsed.board, piece, c.x, c.y, [
      [-1, -1], [1, -1], [-1, 1], [1, 1]
    ]);
  }
  if (kind === 'r') {
    return collectSliderMoves(parsed.board, piece, c.x, c.y, [
      [0, -1], [0, 1], [-1, 0], [1, 0]
    ]);
  }
  if (kind === 'q') {
    return collectSliderMoves(parsed.board, piece, c.x, c.y, [
      [0, -1], [0, 1], [-1, 0], [1, 0], [-1, -1], [1, -1], [-1, 1], [1, 1]
    ]);
  }
  if (kind === 'k') {
    return collectKingMoves(parsed.board, piece, c.x, c.y, parsed.castling, parsed.turn);
  }
  return [];
}
